import { toLocalISO } from './dateFormat';
import { isOrderComplete } from '../constants/statuses';

/**
 * THE classification of a hire line into Upcoming / On hire / Ending soon /
 * Off hired, and the Table view's tab membership built on it. Extracted
 * 2026-09-27 out of `components/hires/KanbanView.js` (where it was inline)
 * when the hires Table view needed the same answer — the Kanban and the Table
 * import this, so the two views cannot disagree about which bucket a hire is
 * in. The Kanban's behaviour is byte-for-byte what it was: proven by
 * `components/hires/KanbanView.columns.test.js`, written and run green against
 * the pre-extraction file and unchanged after.
 *
 * ── The window ─────────────────────────────────────────────────────────
 * `ENDING_SOON_WINDOW_DAYS = 7`: `hire_to` on or before TODAY + 7 (local
 * calendar, `toLocalISO`) is "ending soon". 🔑 **It is the KANBAN's column
 * threshold, not the 3/2/TOMORROW/TODAY countdown** — that countdown is the
 * NOTIFICATION ruling (09-20, server `hireUtils.js`), and `CalendarView.js`
 * carries its own 3-day tint. `server/helpers/hireUrgency.js`'s docblock
 * records the 7 as a column-grouping decision left alone by design. Whether
 * the column should be 3 is an OPEN owner question (2026-09-27); if he says
 * yes it is this one constant, and it moves the Kanban AND the Table.
 *
 * ── Why "ending soon" is not a colour band ─────────────────────────────
 * `utils/hireUrgency.js#getHireUrgencyBand` answers "what colour" (red ≤ 0,
 * amber 1..5). This answers "which list". Do not fold one into the other.
 *
 * ── Rules the Kanban already had, kept on purpose ──────────────────────
 * - `overdue` is ALWAYS ending soon, whatever its `hire_to`, and sorts first.
 * - A still-`on_hire` row whose `hire_to` has PASSED (the nightly status job
 *   has not flipped it yet) is ending soon.
 * - 🪤 A NULL `hire_to` on an active row compares as `''`, which sorts before
 *   every date, so it lands in ending soon. That is what the inline code did;
 *   it is preserved, not endorsed — an unknown return date is at least worth
 *   looking at.
 * - `hire_to` is the LAST HIRED DAY (inclusive), so `hire_to === today` is
 *   still on hire and ending today — ending soon, never off hire.
 * - An unknown `hire_status` is in NO bucket (the Kanban drops it); the
 *   Table's `All` tab still lists it, so nothing the server sent is hidden.
 *
 * ⚠️ Comparisons are on `YYYY-MM-DD` STRINGS, exactly as before; the cutoff is
 * built with `setDate` in LOCAL time, so a DST change inside the window moves
 * nothing.
 *
 * ── Cancelled/rejected ORDERS (2026-09-28 owner ruling) ────────────────
 * *"but maybe we should have a canceld/reject tab? like dept orders?"* — a
 * cancelled order was never hired, so a hire line on one must not read as
 * live: excluded from Upcoming / On hire / Ending soon / Off hire (and their
 * counts), checked BEFORE `hire_status` in `classifyHire` so the hire's own
 * status cannot override its order's. It gets its own Table tab, `cancelled`,
 * mirroring Dept Orders' `Cancelled` (`pages/DepartmentOrders.js`'s
 * `ORDER_QUEUE_TABS`): last in the tab order, and INCLUDED in `All` (Dept
 * Orders' `All` is every row with no predicate, cancelled included — the sum
 * identity its own docblock pins). The Kanban gets the exclusion for free —
 * it destructures only the other four buckets, so `cancelled` is simply never
 * rendered, the same shape it already uses for an unrecognised `hire_status`.
 * Gantt and Calendar have no bucket-based membership test of their own, so
 * their PAGE filters before handing them rows: `excludeCancelledOrderHires`
 * for an `items`-shaped array (Gantt), `cancelledOrderItemIds` to join against
 * a row shape that carries no `order_status` of its own (Calendar's events —
 * `GET /api/hires/calendar` selects a strict subset of `GET /api/hires`'s
 * columns and does not select `o.status`).
 */
export const ENDING_SOON_WINDOW_DAYS = 7;

/** The last `hire_to` (inclusive, `YYYY-MM-DD`) that still counts as ending soon. */
export function endingSoonCutoff(now = new Date()) {
  const d = new Date(now);
  d.setDate(d.getDate() + ENDING_SOON_WINDOW_DAYS);
  return toLocalISO(d);
}

/** The server's own status flag — the ONE overdue test for both views. */
export const isHireOverdue = (item) => item.hire_status === 'overdue';

const isActive = (item) => item.hire_status === 'on_hire' || item.hire_status === 'overdue';

/** An order status whose hires were never really live (see the docblock above). */
const CANCELLED_ORDER_STATUSES = new Set(['cancelled', 'rejected']);

/** True when the hire's ORDER is cancelled or rejected. */
export function isHireOrderCancelled(item) {
  return !!item && CANCELLED_ORDER_STATUSES.has(item.order_status);
}

/**
 * True when the hire's ORDER is complete — `GET /api/hires` sends
 * `o.completed_at` as `order_completed_at` (read-only, 2026-09-29), and the
 * ONE completion predicate is `constants/statuses.js#isOrderComplete`
 * (server truth: `orders.completed_at IS NOT NULL`). Never the line's own
 * fields: a hire row has no `completed_at` of its own.
 */
export function isHireOrderComplete(item) {
  return !!item && isOrderComplete({ completed_at: item.order_completed_at });
}

/**
 * True when any hire line of an order's `items` is not off-hired — the order
 * cannot be marked complete (owner ruling 2026-10-11; the server refuses it
 * too, `PATCH /api/orders/:id/complete` → `hire_still_on_hire`).
 */
export function orderHasHireStillOut(items) {
  return Array.isArray(items) && items.some((i) => !!i && !!i.is_hire && i.hire_status !== 'off_hired');
}

/**
 * `'upcoming' | 'on_hire' | 'ending_soon' | 'off_hired' | 'cancelled' | null`
 * for one row. `on_hire` here means the Kanban's column: active and NOT
 * ending soon. The order-cancelled check runs FIRST — see the docblock above.
 */
export function classifyHire(item, cutoff = endingSoonCutoff()) {
  if (isHireOrderCancelled(item)) return 'cancelled';
  if (item.hire_status === 'pending') return 'upcoming';
  if (item.hire_status === 'off_hired') return 'off_hired';
  if (!isActive(item)) return null;
  if (isHireOverdue(item)) return 'ending_soon';
  return (item.hire_to || '') > cutoff ? 'on_hire' : 'ending_soon';
}

/**
 * The hires a Kanban/Gantt view should render as LIVE — order-cancelled or
 * -rejected hires excluded (2026-09-28 owner ruling above). Kanban needs no
 * caller for this (see the docblock); Gantt has no bucket-based membership
 * test, so its PAGE filters `items` through this before handing them over.
 */
export function excludeCancelledOrderHires(items) {
  return items.filter((item) => !isHireOrderCancelled(item));
}

/**
 * Item ids whose order is cancelled/rejected — for joining against a row
 * shape (Calendar's events) that carries no `order_status` of its own.
 */
export function cancelledOrderItemIds(items) {
  const ids = new Set();
  for (const item of items) {
    if (isHireOrderCancelled(item)) ids.add(item.item_id);
  }
  return ids;
}

const byHireFrom = (a, b) => (a.hire_from || '').localeCompare(b.hire_from || '');
const byHireTo = (a, b) => (a.hire_to || '').localeCompare(b.hire_to || '');
const overdueFirstThenHireTo = (a, b) => {
  if (isHireOverdue(a) && !isHireOverdue(b)) return -1;
  if (isHireOverdue(b) && !isHireOverdue(a)) return 1;
  return byHireTo(a, b);
};
const byReturnedDesc = (a, b) =>
  (b.actual_return_date || b.hire_to || '').localeCompare(a.actual_return_date || a.hire_to || '');

/**
 * The four Kanban columns, each in the Kanban's own order:
 * upcoming by `hire_from` ascending, on hire and ending soon by `hire_to`
 * ascending (ending soon with overdue pinned first), off hired by return date
 * descending. `unclassified` carries rows with an unknown status.
 */
export function bucketHires(items, now = new Date()) {
  const cutoff = endingSoonCutoff(now);
  const out = { upcoming: [], onHire: [], endingSoon: [], offHired: [], cancelled: [], unclassified: [] };
  const key = {
    upcoming: 'upcoming', on_hire: 'onHire', ending_soon: 'endingSoon', off_hired: 'offHired',
    cancelled: 'cancelled',
  };
  for (const item of items) {
    const bucket = classifyHire(item, cutoff);
    out[bucket ? key[bucket] : 'unclassified'].push(item);
  }
  out.upcoming.sort(byHireFrom);
  out.onHire.sort(byHireTo);
  out.endingSoon.sort(overdueFirstThenHireTo);
  out.offHired.sort(byReturnedDesc);
  out.cancelled.sort(byHireFrom);
  return out;
}

/**
 * The Table view's tabs. Owner ruling 2026-09-27: *"All, Upcomming, On hire,
 * Ending soon, Off hire. Presume items on the ending soon tab should also show
 * on the On hire tab"*, and OVERDUE shows on BOTH On hire and Ending soon,
 * flagged red and sorted to the top. So **On hire ⊇ Ending soon ⊇ overdue** —
 * unlike the Kanban, whose On Hire column EXCLUDES ending soon (a board puts a
 * card in one place; a tab is a filter).
 */
export const HIRE_TABLE_TABS = Object.freeze([
  { id: 'all', label: 'All' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'on_hire', label: 'On hire' },
  { id: 'ending_soon', label: 'Ending soon' },
  { id: 'off_hire', label: 'Off hire' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
]);

/**
 * The rows of every tab, in each tab's natural order (overdue first wherever
 * it appears). Takes the result of `bucketHires`, so one classification pass
 * feeds every tab and every count.
 *
 * ── Completed (owner's staging feedback, 2026-09-29) ───────────────────
 * *All · Upcoming · On hire · Ending soon · Off hire · Completed · Cancelled.*
 * Completed = the ORDER is complete (`isHireOrderComplete`); Off hire =
 * returned but NOT complete. Built HERE from the existing buckets, never as a
 * new `bucketHires` key: the Kanban destructures those buckets, so it stays
 * byte-identical (a completed returned line is still in its Off Hired column).
 * - Completed holds every NON-cancelled line on a completed order — returned,
 *   live or unknown status — because that is the definition. A LIVE line on a
 *   completed order (contradictory data: completion normally follows return)
 *   stays on its live tab too; a tab is a filter, the On hire ⊇ Ending soon
 *   precedent, and hiding a still-live hire would be the worse error.
 * - Cancelled wins: an order-cancelled line is in Cancelled only.
 */
export function hireTableTabRows(buckets) {
  const { upcoming, onHire, endingSoon, offHired, cancelled, unclassified } = buckets;
  const notComplete = (row) => !isHireOrderComplete(row);
  return {
    all: [...endingSoon, ...onHire, ...upcoming, ...offHired, ...cancelled, ...unclassified],
    upcoming,
    on_hire: [...endingSoon, ...onHire],
    ending_soon: endingSoon,
    off_hire: offHired.filter(notComplete),
    completed: [...endingSoon, ...onHire, ...upcoming, ...offHired, ...unclassified]
      .filter(isHireOrderComplete)
      .sort(byReturnedDesc),
    cancelled,
  };
}

/** A hire's own Table tab, from its `classifyHire` bucket. */
export const TAB_FOR_BUCKET = Object.freeze({
  upcoming: 'upcoming',
  on_hire: 'on_hire',
  ending_soon: 'ending_soon',
  off_hired: 'off_hire',
  cancelled: 'cancelled',
});

/**
 * The ONE tab a hire is resolved to (a `?hire=` deep link, the "is now in …"
 * toast) — or `null` for an unknown status on an open order (the caller falls
 * back to All). A returned or unknown line on a COMPLETED order lives on
 * Completed: Off hire no longer lists it, so sending it there would miss.
 */
export function hireTableHomeTab(item, cutoff = endingSoonCutoff()) {
  const bucket = classifyHire(item, cutoff);
  if ((bucket === 'off_hired' || bucket === null) && isHireOrderComplete(item)) return 'completed';
  return bucket ? TAB_FOR_BUCKET[bucket] : null;
}
